import React, { useState, useRef, forwardRef } from 'react';

import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';

import { Info } from '../icons';
import clsx from 'clsx';

const App = ({ submitUrl, csrfToken, maxTasks }) => {
  const [shouldAccountWeights, setShouldAccountWeights] = useState(false);
  const [sliderValues, setSliderValues] = useState({
    parameter_s: { min: 5, max: 25, val: 15, step: 5 },
    parameter_p_pi: { min: 5, max: 25, val: 15, step: 1 },
    parameter_p_a: { min: 5, max: 25, val: 15, step: 1 },
    parameter_p_f: { min: 5, max: 25, val: 15, step: 1 },
    weight_p_pi: { min: 0, max: 1, val: 0.5, step: 0.05 },
    weight_p_a: { min: 0, max: 1, val: 0.5, step: 0.05 },
    weight_p_f: { min: 0, max: 1, val: 0.5, step: 0.05 },
  });
  const confirmationModalRef = useRef(null);

  function handleChangeSParam(sValue) {
    setSliderValues((p) => {
      const [otherParamsMin, otherParamsMax] = getNewMinMaxValues(sValue);
      console.log('new min', otherParamsMin, 'newmax', otherParamsMax);
      return {
        ...p,
        parameter_s: { ...p.parameter_s, val: sValue },
        parameter_p_pi: {
          ...p.parameter_p_pi,
          val: sValue,
          min: otherParamsMin,
          max: otherParamsMax,
        },
        parameter_p_a: {
          ...p.parameter_p_a,
          val: sValue,
          min: otherParamsMin,
          max: otherParamsMax,
        },
        parameter_p_f: {
          ...p.parameter_p_f,
          val: sValue,
          min: otherParamsMin,
          max: otherParamsMax,
        },
      };
    });
  }
  function changeNormalSlider(slider, value) {
    setSliderValues((p) => {
      return { ...p, [slider]: { ...p[slider], val: value } };
    });
  }
  function getNewMinMaxValues(sValue) {
    const s = parseInt(sValue);
    const min = s - 10 < 0 ? 0 : s - 10;
    const max = s + 10 > 100 ? 100 : s + 10;
    return [min, max];
  }

  function handleActionButton() {
    confirmationModalRef?.current?.showModal();
  }
  async function submitCalculations() {
    const formData = new FormData();
    Object.keys(sliderValues).forEach((key) => {
      const isWeight = key.startsWith('weight');
      if ((isWeight && shouldAccountWeights) || !isWeight) {
        formData.append(key, sliderValues[key].val);
      } else if (isWeight) {
        // make all weights the same if not taking into account
        // possibly this could be done by making backend explicitly handle
        // "should_account_weights" parameter
        formData.append(key, 1);
      }
    });
    const res = await fetch(submitUrl, {
      method: 'post',
      body: formData,
      headers: {
        'X-CSRFToken': csrfToken,
      },
    });
    const eventSubmit = new CustomEvent('CALCULATION_SUBMITED', {
      detail: {
        status: res.status,
      },
    });
    document.dispatchEvent(eventSubmit);
  }

  return (
    <div className="prose w-full max-w-[--page-content-w] flex flex-col">
      <div className="flex flex-col sm:flex-row sm:items-baseline mb-4">
        <h1 className="mb-0 sm:mb-0">REDUCO2&nbsp;</h1>
        <h2 className="mt-0 mb-0">
          - Narzędzie do projektowania polityki redukcji emisji CO<sub>2</sub>
        </h2>
        <a
          href="https://www.youtube.com/watch?v=C2rFRe6tXc0&list=PLsr7358AKbHrVSgDYDyxCuae8mXe5Risx&index=2"
          target="_blank"
          className="ml-auto btn btn-outline btn-sm gap-2 whitespace-nowrap flex-shrink-0 shadow shadow-primary sm:shadow-none mt-8 sm:mt-0"
          rel="noopener noreferrer"
          title="Zobacz samouczek video"
        >
          <span
            className="hidden"
            dangerouslySetInnerHTML={{
              __html:
                '<!--!Font Awesome Free 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2025 Fonticons, Inc.-->',
            }}
          ></span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 576 512"
            className="h-4 w-4"
            fill="currentColor"
          >
            <path d="M0 128C0 92.7 28.7 64 64 64l256 0c35.3 0 64 28.7 64 64l0 256c0 35.3-28.7 64-64 64L64 448c-35.3 0-64-28.7-64-64L0 128zM559.1 99.8c10.4 5.6 16.9 16.4 16.9 28.2l0 256c0 11.8-6.5 22.6-16.9 28.2s-23 5-32.9-1.6l-96-64L416 337.1l0-17.1 0-128 0-17.1 14.2-9.5 96-64c9.8-6.5 22.4-7.2 32.9-1.6z" />
          </svg>
          Samouczek
        </a>
      </div>
      <div className="flex flex-col md:flex-row gap-4 md:gap-6">
        <p className="flex-1 text-left sm:text-justify mt-0 mb-4 sm:columns-2 sm:gap-10">
          Narzędzie pozwala wskazywać kluczowe elementy polskiego systemu
          gospodarczego, które należy zmodyfikować w celu osiągnięcia zadanego
          przez Użytkownika poziomu redukcji emisji CO<sub>2</sub> w procesach
          produkcyjnych. Narzędzie wykorzystuje tzw. rozszerzony model
          gospodarki narodowej i bazuje na zastosowaniu precyzyjnie dobranej
          metody matematycznej, która prowadzi do uzyskiwania relatywnie łatwych
          w interpretacji wyników pozwalających na formułowanie konkretnych
          zaleceń i wskazówek dla polityki gospodarczej. Przy każdym
          uruchomieniu narzędzia Użytkownik ma możliwość zadania wartości 7
          parametrów sterujących.
          <span className="block mt-4">
            Więcej informacji o algorytmie obliczeniowym znajdziesz w{' '}
            <a
              href="https://www.sciencedirect.com/science/article/pii/S0140988322004613"
              target="_blank"
            >
              tym artykule
            </a>
            .
          </span>
        </p>
      </div>

      <div className="divider my-6"></div>

      <div className="mb-8">
        <p className="text-left sm:text-justify mb-4">
          Idea modelu matematycznego wykorzystywanego w aplikacji REDUCO2 jest
          zilustrowana na poniższym schemacie:
        </p>
        <div className="flex justify-center my-6">
          <img
            src="/static/images/reduco2-schema.png"
            alt="Schemat procesu redukcji emisji CO2"
            className="h-auto rounded-md max-w-[min(800px,100%)]"
          />
        </div>
        <p className="text-left sm:text-justify">
          Użytkownik określa oczekiwany poziom redukcji emisji CO<sub>2</sub> w
          procesach produkcyjnych w polskiej gospodarce wyrażony w [%] (pole
          czarne) a następnie model matematyczny określa sposób osiągniecia tej
          redukcji poprzez wskazanie, w których sektorach należy zastosować
          politykę redukcji popytu (pole niebieskie na schemacie), politykę
          innowacji technologicznej (pole czerwone) oraz politykę redukcji
          zużycia (pole zielone).
        </p>
      </div>

      <h2 className="mt-0 mb-4">
        <strong>
          Wskaż oczekiwany poziom redukcji emisji CO<sub>2</sub> w procesach
          produkcyjnych w polskiej gospodarce wyrażony w [%]
        </strong>
      </h2>
      <Slider
        info={SLIDERS_INFO.parameter_s}
        id={'parameter_s'}
        color={'success'}
        val={sliderValues.parameter_s.val}
        minVal={sliderValues.parameter_s.min}
        maxVal={sliderValues.parameter_s.max}
        step={sliderValues.parameter_s.step}
        onInput={(value) => handleChangeSParam(value)}
      />
      <h2>Parametry sterujące</h2>
      <div className="flex flex-col lg:flex-row gap-12 lg:items-end">
        <Slider
          info={SLIDERS_INFO.parameter_p_pi}
          id={'parameter_p_pi'}
          color={'info'}
          val={sliderValues.parameter_p_pi.val}
          minVal={sliderValues.parameter_p_pi.min}
          maxVal={sliderValues.parameter_p_pi.max}
          step={sliderValues.parameter_p_pi.step}
          onInput={(value) => changeNormalSlider('parameter_p_pi', value)}
        />
        <Slider
          info={SLIDERS_INFO.parameter_p_a}
          id={'parameter_p_a'}
          color={'info'}
          val={sliderValues.parameter_p_a.val}
          minVal={sliderValues.parameter_p_a.min}
          maxVal={sliderValues.parameter_p_a.max}
          step={sliderValues.parameter_p_a.step}
          onInput={(value) => changeNormalSlider('parameter_p_a', value)}
        />
        <Slider
          info={SLIDERS_INFO.parameter_p_f}
          id={'parameter_p_f'}
          color={'info'}
          val={sliderValues.parameter_p_f.val}
          minVal={sliderValues.parameter_p_f.min}
          maxVal={sliderValues.parameter_p_f.max}
          step={sliderValues.parameter_p_f.step}
          onInput={(value) => changeNormalSlider('parameter_p_f', value)}
        />
      </div>
      <h3>Włącz własne wagi i zobacz, jak wpływają na wynik modelu.</h3>
      <CollapsibleContent
        onToggle={() => setShouldAccountWeights(!shouldAccountWeights)}
      >
        <h2>Wagi dla rozpatrywanych polityk</h2>
        <div className="flex flex-col md:flex-row md:gap-10">
          <p className="flex-1 text-left sm:text-justify md:mt-0">
            Za pomocą wag użytkownik może arbitralnie zadać hierarchię
            wprowadzania polityk{' '}
            <TooltipAbbr
              abbreviation="PRP"
              definition="Polityka Redukcji Popytu"
            />
            ,{' '}
            <TooltipAbbr
              abbreviation="PIT"
              definition="Polityka Innowacji Technologicznej"
            />{' '}
            oraz{' '}
            <TooltipAbbr
              abbreviation="PRZ"
              definition="Polityka Redukcji Zużycia"
            />
            . Wyższa waga danej polityki oznacza, że ma ona większe znaczenie.
          </p>
          <p className="flex-1 text-left sm:text-justify mt-1 md:mt-0">
            Wyższa waga wskazuje modelowi, że powinien koncentrować się w
            większym stopniu na wprowadzaniu tej polityki do postaci finalnego
            zestawu rekomendacji.
          </p>
        </div>
        <div className="flex flex-col lg:flex-row gap-20 w-full lg:justify-stretch lg:items-end">
          <Slider
            info={SLIDERS_INFO.weight_p_pi}
            id={'weight_p_pi'}
            color={'secondary'}
            val={sliderValues.weight_p_pi.val}
            minVal={sliderValues.weight_p_pi.min}
            maxVal={sliderValues.weight_p_pi.max}
            step={sliderValues.weight_p_pi.step}
            onInput={(value) => changeNormalSlider('weight_p_pi', value)}
          />
          <Slider
            info={SLIDERS_INFO.weight_p_a}
            id={'weight_p_a'}
            color={'secondary'}
            val={sliderValues.weight_p_a.val}
            minVal={sliderValues.weight_p_a.min}
            maxVal={sliderValues.weight_p_a.max}
            step={sliderValues.weight_p_a.step}
            onInput={(value) => changeNormalSlider('weight_p_a', value)}
          />
          <Slider
            info={SLIDERS_INFO.weight_p_f}
            id={'weight_p_f'}
            color={'secondary'}
            val={sliderValues.weight_p_f.val}
            minVal={sliderValues.weight_p_f.min}
            maxVal={sliderValues.weight_p_f.max}
            step={sliderValues.weight_p_f.step}
            onInput={(value) => changeNormalSlider('weight_p_f', value)}
          />
        </div>
      </CollapsibleContent>
      <div className="flex justify-center mb-5 sm:mb-4 mt-8 sm:mt-14 -mx-4 sm:-mx-6">
        <button
          type="submit"
          className="btn btn-primary btn-lg px-4 sm:px-6 py-3 text-sm sm:text-base font-medium whitespace-nowrap w-full max-w-xs sm:max-w-none sm:w-auto text-white"
          onClick={handleActionButton}
        >
          Uruchom Model Optymalizacji
        </button>
      </div>
      <ConfirmationModal
        ref={confirmationModalRef}
        sliderValues={sliderValues}
        maxTasks={maxTasks}
        shouldAccountWeights={shouldAccountWeights}
        onConfirm={async () => await submitCalculations()}
      />
    </div>
  );
};

const TooltipAbbr = ({ abbreviation, definition }) => {
  return (
    <Tippy
      content={definition}
      placement="top"
      theme="custom-dark"
      appendTo={document.body}
      popperOptions={{
        strategy: 'fixed',
        modifiers: [
          {
            name: 'preventOverflow',
            options: {
              boundary: 'viewport',
              padding: 8,
            },
          },
        ],
      }}
    >
      <span className="underline inline-block whitespace-nowrap align-baseline z-10 cursor-pointer">
        {abbreviation}
      </span>
    </Tippy>
  );
};

const Slider = ({ info, id, color, val, minVal, maxVal, step, onInput }) => {
  function getMiddleTickLabel(min, max) {
    return (max - min) / 2 + min;
  }

  return (
    <section className="w-full lg:w-1/3">
      <h3>
        <label htmlFor={`${id}_slider`} className="flex items-center">
          <span
            className="w-max"
            dangerouslySetInnerHTML={{ __html: info.title }}
          ></span>
          <Tippy content={info.description} placement="top" theme="custom-dark">
            <span className="ml-4 cursor-pointer">
              <Info
                className={clsx({
                  'stroke-info': color === 'info',
                  'stroke-success': color === 'success',
                  'stroke-secondary': color === 'secondary',
                })}
              />
            </span>
          </Tippy>
        </label>
      </h3>
      <div className="flex">
        <div className="flex flex-col w-full">
          <input
            type="range"
            id={`${id}_slider`}
            name={`${id}_slider`}
            className={clsx('range w-full self-stretch', {
              'range-info': color == 'info',
              'range-success': color === 'success',
              'range-secondary': color === 'secondary',
            })}
            min={minVal}
            max={maxVal}
            step={step}
            value={val}
            onInput={(event) => onInput(event.target.value)}
          />
          <div className="flex w-full justify-between px-2 text-xs">
            <span>|</span>
            <span>|</span>
            <span>|</span>
          </div>
          <div className="flex w-full justify-between">
            <span className="w-12 text-start ml-1">{minVal}</span>
            <span className="w-12 text-center">
              {getMiddleTickLabel(minVal, maxVal)}
            </span>
            <span className="w-12 text-end">{maxVal}</span>
          </div>
        </div>
        <span className="flex flex-nowrap w-20 mr-2 line-clamp-1 -mt-1 font-bold">
          &nbsp;=&nbsp; {val}
        </span>
      </div>
    </section>
  );
};

const CollapsibleContent = ({ onToggle, children }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);

  return (
    <>
      <label className="collapse-title flex items-center cursor-pointer">
        <input
          type="checkbox"
          className="checkbox checkbox-primary mr-2"
          checked={!isCollapsed}
          onChange={() => {
            setIsCollapsed(!isCollapsed);
            onToggle();
          }}
        />
        Uwzględnij własne wagi czynników
      </label>
      <div
        className={clsx('transition-[max-height] duration-300', {
          'max-h-0 overflow-hidden': isCollapsed,
          'max-h-[2000px] overflow-visible': !isCollapsed,
        })}
      >
        {children}
      </div>
    </>
  );
};

const SLIDERS_INFO = {
  parameter_s: {
    title: 'Oczekiwany poziom redukcji emisji CO<sub>2</sub> [%]',
    description:
      'Parametr ten określa zakładany przez Użytkownika procentowy spadek emisji dwutlenku węgla powstającej na etapie produkcji dóbr i usług w polskiej gospodarce, który ma nastąpić w konsekwencji zastosowania wskazanej przez model kombinacji polityk gospodarczych.',
  },
  parameter_p_pi: {
    title: 'Polityka Innowacji Technologicznej [%]',
    description:
      'Parametr ten określa zakładaną przez Użytkownika wielkość redukcji emisji dwutlenku węgla na jednostkę produkcji we wskazanych przez Narzędzie sektorach polskiej gospodarki. Parametr ten pozwala wprowadzać do modelu tzw. politykę innowacji technologicznej (PIT) prowadzącą do redukcji emisji dwutlenku węgla w procesach produkcyjnych poprzez obniżenie bezpośredniej emisyjności sektorów.',
  },
  parameter_p_a: {
    title: 'Polityka Redukcji Zużycia [%]',
    description:
      'Parametr ten określa zakładaną przez Użytkownika wielkość redukcji współczynnika bezpośredniej produktochłonności produkcji dla wskazanych przez Narzędzie par sektorów polskiej gospodarki. Parametr ten pozwala wprowadzać do modelu tzw. politykę redukcji zużycia (PRZ) prowadzącą do redukcji emisji dwutlenku węgla w procesach produkcyjnych poprzez ograniczanie wybranych transakcji międzysektorowych.',
  },
  parameter_p_f: {
    title: 'Polityka Redukcji Popytu [%]',
    description:
      'Parametr ten określa zakładaną przez Użytkownika wielkość redukcji popytu finalnego (a więc konsumpcji gospodarstw domowych, wydatków rządowych, eksportu) w wybranych przez Narzędzie sektorach polskiej gospodarki. Parametr ten pozwala wprowadzać do modelu tzw. politykę redukcji popytu (PRP) prowadzącą do redukcji emisji dwutlenku węgla w procesach produkcyjnych poprzez ograniczanie zużycia końcowego w wybranych sektorach.',
  },
  weight_p_pi: {
    title: 'Waga PIT',
    description: 'Polityka Innowacji Technologicznej',
  },
  weight_p_a: {
    title: 'Waga PRZ',
    description: 'Polityka Redukcji Zużycia',
  },
  weight_p_f: {
    title: 'Waga PRP',
    description: 'Polityka Redukcji Popytu',
  },
};

const ConfirmationModal = forwardRef(function ConfirmationModal(
  { sliderValues, maxTasks: maxTasksProp, shouldAccountWeights, onConfirm },
  ref,
) {
  const maxTasks = parseInt(maxTasksProp, 10);
  return (
    <dialog className="modal" ref={ref}>
      <div className="modal-box w-full max-w-2xl p-0 max-h-[90vh] flex flex-col bg-base-100">
        <header className="px-6 pt-6 pb-3">
          <h3 className="font-bold text-2xl mt-0 mb-3">
            Potwierdź dodanie zadania do kolejki obliczeń
          </h3>
          <p className="m-0 sm:mt-6 text-base-content/80 modal-dialog-note">
            <span className="text-warning font-bold">Uwaga!</span>{' '}
            <span className="underline">
              Po dodaniu zadanie będzie liczyć się do momentu wykonania, bez
              możliwości korekty ani odwołania przez użytkownika. Użytkownik
              może mieć jednocześnie maksymalnie {maxTasks} zadania w kolejce.
            </span>
          </p>
        </header>
        <div className="modal-section-content px-6 py-5 space-y-4 overflow-y-auto">
          <div>
            <div className="font-semibold text-base mb-1 modal-section-label">
              Ustawione parametry:
            </div>
            <div className="overflow-x-auto">
              <table className="table mb-0">
                <thead>
                  <tr className="bg-gray-50">
                    <th>Poziom redukcji</th>
                    <th>PIT</th>
                    <th>PRZ</th>
                    <th>PRP</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th className="font-normal">
                      {sliderValues.parameter_s.val}
                    </th>
                    <td>{sliderValues.parameter_p_pi.val}</td>
                    <td>{sliderValues.parameter_p_a.val}</td>
                    <td>{sliderValues.parameter_p_f.val}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          {shouldAccountWeights && (
            <div>
              <div className="font-semibold text-base mb-1 modal-section-label">
                Ustawione wagi:
              </div>
              <div className="overflow-x-auto">
                <table className="table mb-0">
                  <thead>
                    <tr className="bg-gray-50">
                      <th>PIT</th>
                      <th>PRZ</th>
                      <th>PRP</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{sliderValues.weight_p_pi.val}</td>
                      <td>{sliderValues.weight_p_a.val}</td>
                      <td>{sliderValues.weight_p_f.val}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        <div className="modal-action modal-action--safe-bottom justify-between gap-3 px-6 py-4 border-t border-base-200 bg-base-100">
          <form method="dialog">
            <button className="btn btn-secondary">Anuluj</button>
          </form>
          <button
            className="btn btn-success"
            onClick={() => {
              ref?.current.close();
              onConfirm();
            }}
          >
            Dodaj
          </button>
        </div>
      </div>
    </dialog>
  );
});

export default App;
